/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
//Parameters required to build a Sisense dashboard URL.
interface URLParams {
  themeParam: string;
  lang: string;
  showLeftPanel: boolean;
  featuresQuery: string;
};

/**
 * Builds a full IFrame URL for a dashboard using a specific OID.
 *
 * ```typescript
 * const url = getOIDURL({
 *   oid: 'abc123',
 *   themeParam: '&theme=dark',
 *   lang: 'en',
 *   showLeftPanel: true,
 *   featuresQuery: '&features=map,chart'
 * });
 * ```
 *
 * @param params - Combined parameters including OID and shared URL options.
 * @returns A complete IFrame URL pointing to the specified dashboard.
 */
export const getOIDURL = ({
  oid,
  themeParam,
  lang,
  showLeftPanel,
  featuresQuery
}: URLParams & { oid: string }) => (
  `app/main/dashboards/${oid}?embed=false${themeParam}&locale=${lang}&l=${showLeftPanel}&h=false&t=true&filters=all${featuresQuery}`
);

/**
 * Builds a URL for accessing the Report Manager plugin view.
 *
 * ```typescript
 * const url = getReportsURL('&theme=light', 'es');
 * ```
 *
 * @param themeParam - Optional theme string (e.g., `&theme=light`)
 * @param lang - Current language code (e.g., 'en', 'fr')
 * @returns A URL string to load the Report Manager plugin.
 */
export const getReportsURL = (themeParam: string, lang: string) => (
  `reportManager/main.html?embed=true${themeParam}&locale=${lang}&l=true&h=false&t=true&filters=all#/reports`
);

/**
 * Builds the default dashboard URL, usually used when no OID or report manager is active.
 *
 * ```typescript
 * const url = getDefaultURL({
 *   route: 'home',
 *   themeParam: '',
 *   lang: 'fr',
 *   showLeftPanel: false,
 *   featuresQuery: ''
 * });
 * ```
 *
 * @param params - Parameters including route and standard URL options.
 * @returns A URL pointing to the home page or default dashboard.
 */
export const getDefaultURL = ({
  route,
  themeParam,
  lang,
  showLeftPanel,
  featuresQuery
}: URLParams & { route: string }) => (
  `app/main/${route}?embed=false${themeParam}&locale=${lang}&l=${showLeftPanel}&h=false&t=true&filters=all${featuresQuery}`
);
